import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, User, Phone as PhoneIcon } from "lucide-react";
import { toast } from "sonner";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AuthCard from "@/components/jea/AuthCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import JeaCheckbox from "@/components/jea/JeaCheckbox";
import PillButton from "@/components/jea/PillButton";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/apiClient";

const ACCOUNT_COPY = {
  standard: "Manage up to 3 judgments with guided workflows",
  pro: "Multi-judgment dashboard with advanced mode for firms and agencies",
};

const RegisterPage = () => {
  const nav = useNavigate();
  const { register } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [accountType, setAccountType] = useState("standard");
  const [tos, setTos] = useState(false);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const desc = useMemo(() => ACCOUNT_COPY[accountType], [accountType]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!fullName.trim() || !password || !confirm) {
      setError("Full name and password are required.");
      return;
    }
    const emailTrimmed = email.trim();
    const phoneTrimmed = (phone || "").trim();
    if (!emailTrimmed && !phoneTrimmed) {
      setError("Please provide an email, a phone number, or both.");
      return;
    }
    if (phoneTrimmed && !isValidPhoneNumber(phoneTrimmed)) {
      setError("Enter a valid phone number including the country code.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!tos) {
      setError("You must agree to the Terms of Service.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        full_name: fullName.trim(),
        password,
        account_type: accountType,
      };
      if (emailTrimmed) payload.email = emailTrimmed.toLowerCase();
      if (phoneTrimmed) payload.phone = phoneTrimmed;
      const data = await register(payload);
      toast.success("Account created — verify your contact info to continue.");
      nav("/verify-contact", {
        state: {
          pending_token: data.pending_token,
          user_id: data.user_id,
          email: payload.email || null,
          phone: payload.phone || null,
          verification_status: data.auto_sent?.verification_status || {},
          restored: !!data.auto_sent?.restored,
        },
      });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthCard>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <JeaInput
          placeholder="Full Name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          leftIcon={<User size={16} />}
          autoComplete="name"
          data-testid="register-fullname-input"
        />
        <div
          className="text-[12px] text-text-secondary bg-bg-tertiary/60 border border-border-default rounded-md px-3 py-2 leading-relaxed"
          data-testid="register-contact-hint"
        >
          Provide at least one — you'll use this to recover your account.
        </div>
        <JeaInput
          type="email"
          placeholder="Email (optional if phone is provided)"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          leftIcon={<Mail size={16} />}
          autoComplete="email"
          data-testid="register-email-input"
        />
        <div
          className="rounded-md border border-border-default bg-bg-tertiary/60 px-3 py-2"
          data-testid="register-phone-wrapper"
        >
          <div className="flex items-center gap-2 text-[12px] text-text-secondary mb-1">
            <PhoneIcon size={14} /> Phone (optional if email is provided)
          </div>
          <PhoneInput
            international
            defaultCountry="US"
            countries={["US"]}
            addInternationalOption={false}
            countrySelectProps={{ disabled: true }}
            countryCallingCodeEditable={false}
            limitMaxLength
            value={phone}
            onChange={(v) => setPhone(v || "")}
            placeholder="(555) 555-0123"
            data-testid="register-phone-input"
            className="jea-phone-input"
          />
          <div
            className="text-[11px] text-text-muted mt-1"
            data-testid="register-phone-help"
          >
            US numbers only right now — international support coming soon.
          </div>
        </div>
        <JeaInput
          type={show ? "text" : "password"}
          placeholder="Password (min 8 chars)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          leftIcon={<Lock size={16} />}
          autoComplete="new-password"
          data-testid="register-password-input"
          rightSlot={
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="p-2 rounded-md text-text-secondary hover:text-accent-pink transition-colors"
              aria-label={show ? "Hide password" : "Show password"}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />
        <JeaInput
          type={show ? "text" : "password"}
          placeholder="Confirm Password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          leftIcon={<Lock size={16} />}
          autoComplete="new-password"
          data-testid="register-confirm-input"
        />

        <div className="pt-2">
          <div className="jea-caption mb-2 text-text-secondary">Account Type</div>
          <div className="flex gap-2">
            <PillButton
              active={accountType === "standard"}
              onClick={() => setAccountType("standard")}
              data-testid="register-account-standard"
            >
              Standard
            </PillButton>
            <PillButton
              active={accountType === "pro"}
              onClick={() => setAccountType("pro")}
              data-testid="register-account-pro"
            >
              PRO
            </PillButton>
          </div>
          <p
            className="text-[13px] text-text-secondary text-center mt-2"
            data-testid="register-account-desc"
          >
            {desc}
          </p>
        </div>

        <div className="pt-1">
          <JeaCheckbox
            id="tos"
            checked={tos}
            onChange={setTos}
            data-testid="register-tos-checkbox"
            label={
              <span>
                I agree to the{" "}
                <a href="#" className="text-accent-pink hover:underline" onClick={(e) => e.preventDefault()}>
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="#" className="text-accent-pink hover:underline" onClick={(e) => e.preventDefault()}>
                  Privacy Policy
                </a>
                .
              </span>
            }
          />
        </div>

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="register-error"
          >
            {error}
          </div>
        )}

        <JeaButton type="submit" size="lg" fullWidth loading={loading} data-testid="register-submit-button">
          Create Account
        </JeaButton>

        <div className="pt-3 text-center">
          <Link
            to="/login"
            className="text-[13px] text-text-secondary hover:text-accent-pink transition-colors"
            data-testid="register-back-to-login"
          >
            Already have an account? <span className="text-accent-pink font-semibold">Log in</span>
          </Link>
        </div>
      </form>
    </AuthCard>
  );
};

export default RegisterPage;
